import React, { useEffect, Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { DarkModeProvider } from './context/DarkModeContext';
import OfflineIndicator from './components/OfflineIndicator';
import GlobalModal, { showGlobalModal } from './components/GlobalModal';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import DailyNotificationManager from './components/DailyNotificationManager';
import NotificationTestButton from './components/NotificationTestButton';
import './i18n'; // Import i18n configuration

// Lazy load components for better performance
const AppRouter = lazy(() => import('./routes/AppRouter'));
const FloatingAiButton = lazy(() => import('./components/FloatingAiButton'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Create a QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Reduced retries from 2 to 1 for faster failure
      retryDelay: 500, // Faster retry delay (500ms instead of exponential backoff)
      staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes (reduced from 5)
      gcTime: 5 * 60 * 1000, // Cache data for 5 minutes (reduced from 10)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch when connection is restored
      networkMode: 'online', // Only run queries when online for better performance
    },
  },
});

function App() {
  // Expose showGlobalModal globally for use in non-React contexts (e.g., service worker)
  useEffect(() => {
    window.showGlobalModal = showGlobalModal;
    return () => {
      delete window.showGlobalModal;
    };
  }, []);

  return (
    <DarkModeProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <FavoritesProvider>
              <PerformanceOptimizer />
              <Suspense fallback={<LoadingSpinner />}>
                <AppRouter />
              </Suspense>
              <Suspense fallback={null}>
                <FloatingAiButton />
              </Suspense>
              <OfflineIndicator />
              <GlobalModal />
              <PWAInstallPrompt />
              <DailyNotificationManager />
              <NotificationTestButton />
            </FavoritesProvider>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </DarkModeProvider>
  );
}

export default App;
